import React, { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { eachDayOfInterval, format, parseISO } from "date-fns";

import ActionButton from "./ActionButton";
import { useSnackbar } from "./snackbarProvider.jsx";
import useGraphQLClient from "../hooks/useGraphQLClient";
import { CREATE_HOLIDAY_REQUEST } from "../graph/holidayManagement/mutations.js";

const CreateHolidayRequestModal = ({
  open,
  onClose,
  onCreated,
  tenantID,
  staffTenantUserID,
  leaveYear,
  workPattern,
}) => {
  const { setSnackbar } = useSnackbar();
  const client = useGraphQLClient();

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [requestedMinutes, setRequestedMinutes] = useState("");
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    if (submitting) return;

    setError("");
    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!startDate || !endDate) {
      setError("Select a start date and an end date.");
      return;
    }

    if (endDate < startDate) {
      setError("The end date must be on or after the start date.");
      return;
    }

    if (
      !leaveYear ||
      startDate < leaveYear.startDate ||
      endDate >= leaveYear.endDate
    ) {
      setError("The requested dates must fall within the selected leave year.");
      return;
    }

    if (!workPattern) {
      setError("A work pattern is required before requesting holiday.");
      return;
    }

    const partialMinutes =
      requestedMinutes === "" ? null : Number(requestedMinutes);

    if (
      partialMinutes !== null &&
      (!Number.isInteger(partialMinutes) || partialMinutes <= 0)
    ) {
      setError("Requested minutes must be a positive whole number.");
      return;
    }

    const requestDays = [];

    for (const day of eachDayOfInterval({
      start: parseISO(startDate),
      end: parseISO(endDate),
    })) {
      const date = format(day, "yyyy-MM-dd");
      const dayName = format(day, "EEEE").toUpperCase();
      const patternDay = workPattern.days?.find(
        (item) => item.dayOfWeek === dayName
      );

      const patternIsEffective =
        date >= workPattern.effectiveFrom &&
        (!workPattern.effectiveTo || date <= workPattern.effectiveTo);

      if (!patternIsEffective || !patternDay?.isWorkingDay) {
        continue;
      }

      const scheduledMinutes = patternDay.scheduledMinutes || 0;

      if (partialMinutes !== null && partialMinutes > scheduledMinutes) {
        setError(
          `Requested minutes exceed the scheduled work time on ${date}.`
        );
        return;
      }

      requestDays.push({
        date,
        requestedMinutes: partialMinutes,
      });
    }

    if (requestDays.length === 0) {
      setError("The selected dates contain no scheduled working days.");
      return;
    }

    setSubmitting(true);

    try {
      await client.request(CREATE_HOLIDAY_REQUEST, {
        input: {
          tenantID,
          staffTenantUserID,
          leaveYearID: leaveYear.id,
          days: requestDays,
          comment: comment.trim() || null,
        },
      });

      setSnackbar({
        open: true,
        severity: "success",
        message: "Holiday request submitted",
      });

      setStartDate("");
      setEndDate("");
      setRequestedMinutes("");
      setComment("");
      onClose();

      if (onCreated) {
        await onCreated();
      }
    } catch (err) {
      const message =
        err?.response?.errors?.[0]?.message ||
        err?.message ||
        "Failed to submit holiday request";

      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Request Holiday</DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Choose the dates you want to request. Public holidays are excluded
            from your allowance calculation.
          </Typography>

          <TextField
            fullWidth
            required
            type="date"
            label="Start date"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: leaveYear?.startDate,
              max: leaveYear?.endDate
                ? format(
                    new Date(`${leaveYear.endDate}T00:00:00`),
                    "yyyy-MM-dd"
                  )
                : undefined,
            }}
            margin="normal"
          />

          <TextField
            fullWidth
            required
            type="date"
            label="End date"
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: startDate || leaveYear?.startDate,
              max: leaveYear?.endDate
                ? format(
                    new Date(
                      new Date(`${leaveYear.endDate}T00:00:00`).getTime() -
                        24 * 60 * 60 * 1000
                    ),
                    "yyyy-MM-dd"
                  )
                : undefined,
            }}
            margin="normal"
          />

          <TextField
            fullWidth
            type="number"
            label="Minutes per working day"
            value={requestedMinutes}
            onChange={(event) => setRequestedMinutes(event.target.value)}
            helperText="Leave blank to request each full scheduled workday. Enter minutes for a partial day; this amount applies to each working day in the range."
            inputProps={{ min: 1, step: 1 }}
            margin="normal"
          />

          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Comment"
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            margin="normal"
          />

          {error && (
            <Typography color="error" variant="body2" mt={1}>
              {error}
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <ActionButton
            type="button"
            onClick={handleClose}
            tone="ghost"
            disabled={submitting}
          >
            Cancel
          </ActionButton>

          <ActionButton type="submit" tone="gold" disabled={submitting}>
            {submitting ? "Submitting..." : "Submit Request"}
          </ActionButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateHolidayRequestModal;